'use client'

import React, { useState, useEffect } from 'react'
import { X, Eye, Search, ChevronDown, ChevronRight } from 'lucide-react'
import { getAllTherapistsAction } from '@/app/actions/data'
import { useUser } from '@/contexts/user/UserContext'
import { logger } from '@/lib/services/logger.service'

// Content reviewers have isAdmin but shouldn't have impersonation access
const CONTENT_REVIEWER_IDS = ['e9db8020-529b-48f4-887a-a26dada65aa2']

interface TherapistOption {
  id: string
  authId: string
  firstName: string
  lastName: string
  email: string
  cohort: string | null
  isTherapist: boolean | null
  isSupervisor: boolean | null
  isSeniorSupervisor?: boolean | null
  employmentStatus: string | null
}

export function TherapistImpersonation() {
  const { profile, impersonate, impersonatedProfile } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const [therapists, setTherapists] = useState<TherapistOption[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  
  // Debug logging in useEffect to avoid duplicate logs
  useEffect(() => {
    logger.debug('[TherapistImpersonation] Component state:', {
      profile: profile?.email,
      isAdmin: profile?.isAdmin,
      impersonatedProfile: impersonatedProfile?.email,
      showBanner: !!impersonatedProfile,
      showButton: !impersonatedProfile && profile?.isAdmin
    })
  }, [profile?.email, profile?.isAdmin, impersonatedProfile?.email])

  // Fetch therapist list once — persists in React state across open/close
  useEffect(() => {
    if (isOpen && therapists.length === 0) {
      loadTherapists()
    }
  }, [isOpen])

  const loadTherapists = async () => {
    if (therapists.length > 0) return // Already loaded

    setIsLoading(true)
    try {
      let therapistsToLoad: any[] = []

      // Special case: These specific users can view all therapists
      if (profile?.id === 'd0ed67a9-5d67-4cc7-8186-ddc4abb0cbd8' ||
          profile?.id === 'e23d3636-d45a-42bd-b426-d51b8e1d2ef6') {
        therapistsToLoad = await getAllTherapistsAction()
      }
      // Admins can see all therapists
      else if (profile?.isAdmin) {
        therapistsToLoad = await getAllTherapistsAction()
      }
      // Senior supervisors can see all residents
      else if (profile?.isSeniorSupervisor) {
        therapistsToLoad = await getAllTherapistsAction({ employmentStatus: 'RESIDENT' })
      }
      // Regular supervisors can only see therapists they supervise
      else if (profile?.isSupervisor) {
        therapistsToLoad = await getAllTherapistsAction({ supervisorId: profile.id })
      }

      const formattedTherapists = therapistsToLoad.map((t: any) => ({
        id: t.id,
        authId: t.authId,
        firstName: t.firstName || '',
        lastName: t.lastName || '',
        email: t.email || '',
        cohort: t.cohort,
        isTherapist: t.isTherapist,
        isSupervisor: t.isSupervisor,
        employmentStatus: t.employmentStatus
      }))
      setTherapists(formattedTherapists)
    } catch (error) {
      logger.error('Failed to load therapist list for impersonation', error, {
        component: 'TherapistImpersonation',
        action: 'loadTherapists',
        errorType: (error as any)?.code || 'unknown',
        errorMessage: (error as any)?.message,
        isSupervisor: profile?.isSupervisor,
        isAdmin: profile?.isAdmin
      })
    } finally {
      setIsLoading(false)
    }
  }

  const filteredTherapists = therapists.filter(t => {
    const searchLower = searchTerm.toLowerCase()
    return (
      t.firstName.toLowerCase().includes(searchLower) ||
      t.lastName.toLowerCase().includes(searchLower) ||
      t.email.toLowerCase().includes(searchLower) ||
      (t.cohort && t.cohort.toLowerCase().includes(searchLower)) ||
      (t.employmentStatus && t.employmentStatus.toLowerCase().includes(searchLower))
    )
  })

  // Group therapists
  const groupedTherapists = filteredTherapists.reduce((groups, therapist) => {
    let grouped = false
    
    // Supervisors (regardless of other attributes)
    if (therapist.isSupervisor) {
      if (!groups['Supervisors']) groups['Supervisors'] = []
      groups['Supervisors'].push(therapist)
      grouped = true
    }
    // FTAs are isTherapist and either EMPLOYEE or CONTRACTOR (but not supervisors)
    else if (therapist.isTherapist && (therapist.employmentStatus === 'EMPLOYEE' || therapist.employmentStatus === 'CONTRACTOR')) {
      if (!groups['FTAs']) groups['FTAs'] = []
      groups['FTAs'].push(therapist)
      grouped = true
    } 
    // Residents are grouped by cohort
    else if (therapist.cohort && therapist.employmentStatus === 'RESIDENT') {
      const cohortKey = `Cohort ${therapist.cohort}`
      if (!groups[cohortKey]) groups[cohortKey] = []
      groups[cohortKey].push(therapist)
      grouped = true
    }
    // Others
    if (!grouped) {
      if (!groups['Other']) groups['Other'] = []
      groups['Other'].push(therapist)
    }
    return groups
  }, {} as Record<string, TherapistOption[]>)

  // Sort groups: Supervisors first, then FTAs, then cohorts in order, then Others
  const sortedGroupKeys = Object.keys(groupedTherapists).sort((a, b) => {
    if (a === 'Supervisors') return -1
    if (b === 'Supervisors') return 1
    if (a === 'FTAs') return -1
    if (b === 'FTAs') return 1
    if (a === 'Other') return 1
    if (b === 'Other') return -1
    // Sort cohorts
    return a.localeCompare(b)
  })

  const handleImpersonate = async (therapist: TherapistOption) => {
    await impersonate(therapist.authId)
    setIsOpen(false)
    setSearchTerm('')
  }

  const toggleGroup = (groupName: string) => {
    const newCollapsed = new Set(collapsedGroups)
    if (newCollapsed.has(groupName)) {
      newCollapsed.delete(groupName)
    } else {
      newCollapsed.add(groupName)
    }
    setCollapsedGroups(newCollapsed)
  }

  // Show for admins, supervisors, senior supervisors, special users, or when impersonating
  // Content reviewers have isAdmin but shouldn't see impersonation
  const isContentReviewer = profile?.id && CONTENT_REVIEWER_IDS.includes(profile.id)
  const isSpecialUser = profile?.id === 'd0ed67a9-5d67-4cc7-8186-ddc4abb0cbd8' ||
                        profile?.id === 'e23d3636-d45a-42bd-b426-d51b8e1d2ef6'
  const hasAdminAccess = profile?.isAdmin && !isContentReviewer
  if (!hasAdminAccess && !profile?.isSupervisor && !profile?.isSeniorSupervisor &&
      !isSpecialUser && !impersonatedProfile) return null


  return (
    <>
      {/* View As Button */}
      {!impersonatedProfile && (hasAdminAccess || profile?.isSupervisor || profile?.isSeniorSupervisor || isSpecialUser) && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-40 px-4 py-2 bg-purple-600 text-white rounded-lg shadow-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          View as Therapist
        </button>
      )}

      {/* Therapist Selection Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {hasAdminAccess || isSpecialUser
                    ? 'Select Therapist to View As'
                    : profile?.isSeniorSupervisor
                      ? 'Select Resident to View As'
                      : 'Select Supervised Therapist to View As'}
                </h2>
                <button
                  onClick={() => {
                    setIsOpen(false)
                    setSearchTerm('')
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Search */}
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, cohort, or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Therapist List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : sortedGroupKeys.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No therapists found
                </div>
              ) : (
                <div className="p-2">
                  {sortedGroupKeys.map((groupName) => {
                    const isCollapsed = collapsedGroups.has(groupName)
                    const groupCount = groupedTherapists[groupName]?.length || 0
                    
                    return (
                      <div key={groupName} className="mb-2">
                        <button
                          onClick={() => toggleGroup(groupName)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {isCollapsed ? (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            )}
                            <h3 className="text-sm font-medium text-gray-700">
                              {groupName}
                            </h3>
                          </div>
                          <span className="text-xs text-gray-500">
                            {groupCount} {groupCount === 1 ? 'therapist' : 'therapists'}
                          </span>
                        </button>
                        
                        {!isCollapsed && (
                          <div className="mt-1 ml-6">
                            {groupedTherapists[groupName]?.map((therapist) => (
                              <button
                                key={therapist.id}
                                onClick={() => handleImpersonate(therapist)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded transition-colors"
                              >
                                <p className="text-sm text-gray-900">
                                  {therapist.firstName} {therapist.lastName}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}